import PropTypes from 'prop-types';
import { useState, useEffect, useRef } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { Button, IconButton } from '@ui/button';
import { MultiSelect } from '@web/atoms';
import { ResponsiveImage } from '@web/molecules/ResponsiveImage';
import { useShopifyCartContext } from '../OnboardingFlowContainer/ShopifyCartProvider';
import { Question } from '../Question';
import { StyledFormWithImage } from '../StyledForm';
import { ApiResult } from '../../api';
import { useReporter } from '../../reporter';
import { useSessionCustomer } from '../../customer';
import {
  onboardingSubmitted,
  onboardingBackArrowClicked,
  onboardingBreedPageViewed,
  onboardingBreedSelected,
  onboardingBreedPageContinued,
} from '../events';
import { useGetBreeds } from '../../breeds';
import { UNKNOWN_ADORABLE_MIX } from '../../breeds/constants';
import {
  FieldWrapper,
  ImageContainer,
  ArrowContainer,
  MobileImageContainer,
  DesktopImageContainer,
  ButtonContainer,
  BreedFormFieldsContainer,
  BreedFormWrapper,
  Subheadline,
} from './styles.js';
import { schema } from './schema';

export { schema } from './schema';

export const PupBreedForm = ({
  defaultValues,
  goBack,
  index,
  labelBreed,
  onSubmit,
  ctaButtonText = 'Continue',
  subheadline,
  pupBreedQuestionCaption,
  pupBreedQuestionPrompt,
  pupBreedCallout,
  mobileImage,
  desktopImage,
  flowType,
}) => {
  const fetchBreeds = useGetBreeds();
  const breedsRef = useRef(null);

  const getBreeds = async term => {
    const breeds = await fetchBreeds(term);

    if (!term) {
      breedsRef.current = breeds;
    }

    return breeds;
  };

  const {
    handleSubmit,
    control,
    formState: { isSubmitting, isSubmitted, isSubmitSuccessful },
    reset,
    getValues,
  } = useForm({
    mode: 'onTouched',
    defaultValues: defaultValues,
    resolver: yupResolver(schema),
  });

  const isInSubmittingState =
    isSubmitting || (isSubmitted && isSubmitSuccessful);

  const hasCallout = Boolean(pupBreedCallout);
  const [isInputFocused, setIsInputFocused] = useState(false);

  const reporter = useReporter();
  const { customer } = useSessionCustomer();
  const { cart } = useShopifyCartContext();

  useEffect(() => {
    const currentCustomer = cart?.customer || customer;
    reporter.tag(onboardingBreedPageViewed(flowType, currentCustomer));
  }, [reporter, flowType, cart?.customer, customer]);

  const goBackWithReporter = () => {
    reporter.tag(
      onboardingBackArrowClicked('pupbreed_step', cart?.customer || customer),
    );
    goBack();
  };

  const submitForm = async data => {
    const currentCustomer = cart?.customer || customer;
    let breeds = data.breeds ?? [];

    if (!breeds.length) {
      const allBreeds = breedsRef.current ?? (await fetchBreeds());
      const unknownMix = allBreeds.find(
        breed => breed.label === UNKNOWN_ADORABLE_MIX,
      );
      breeds = unknownMix ? [unknownMix] : [];
    }

    const normalizedData = { ...data, breeds };

    reporter.tag(
      onboardingSubmitted(
        index,
        'dog_breed',
        currentCustomer,
        normalizedData,
        flowType,
      ),
    );

    const breedsList = breeds.map(breed => breed.label);
    reporter.tag(onboardingBreedPageContinued(breedsList, flowType));

    const result = await onSubmit(normalizedData);

    ApiResult.ifError(result, () =>
      setTimeout(() =>
        reset(getValues(), {
          keepIsSubmitted: false,
        }),
      ),
    );
  };

  return (
    <StyledFormWithImage onSubmit={handleSubmit(submitForm)}>
      <ImageContainer>
        <ArrowContainer aria-live="polite">
          <IconButton
            aria-label="go to previous step"
            icon={{
              iconName: 'arrow-left',
              size: '24px',
              alt: 'back',
              id: 'prev_step_footer',
              color: 'background.lightest',
            }}
            onClick={goBackWithReporter}
            disabled={isInSubmittingState}
            type="button"
            variant="primary"
          />
        </ArrowContainer>
        <MobileImageContainer>
          <ResponsiveImage priority={true} {...mobileImage} width={400} />
        </MobileImageContainer>
        <DesktopImageContainer>
          <ResponsiveImage
            priority={true}
            {...desktopImage}
            height={362}
            width={406}
          />
        </DesktopImageContainer>
      </ImageContainer>
      <BreedFormWrapper
        $minHeight={hasCallout && isInputFocused ? '450' : undefined}
      >
        <BreedFormFieldsContainer $maxWidth={391}>
          <Question
            prompt={pupBreedQuestionPrompt}
            caption={pupBreedQuestionCaption}
            callout={pupBreedCallout}
            calloutMargin={hasCallout ? [3, 0, 0] : undefined}
            captionMargin={[6, 0, 0, 0]}
            gap={0}
            padding={[0, 0, 0, 0]}
            hideCaptionOrCallout={isInputFocused}
            typography={'heading4'}
          >
            {subheadline && <Subheadline>{subheadline}</Subheadline>}
            <FieldWrapper>
              <Controller
                control={control}
                name="breeds"
                render={({
                  field: {
                    ref,
                    onBlur: fieldOnBlur,
                    onChange: fieldOnChange,
                    ...field
                  },
                  fieldState: { error },
                }) => (
                  <MultiSelect
                    {...field}
                    searchLabel={labelBreed}
                    values={getBreeds}
                    selectedValues={field.value}
                    onboardingBorders
                    max={{ count: 2, message: 'Maximum of two breeds allowed' }}
                    backgroundColor={'background.base'}
                    error={error?.message}
                    onChange={selectedItems => {
                      fieldOnChange(selectedItems);
                      setIsInputFocused(false);

                      const breedNames = selectedItems.map(item => item.label);
                      reporter.tag(onboardingBreedSelected(breedNames));
                    }}
                    onFocus={() => setIsInputFocused(true)}
                    onBlur={() => {
                      fieldOnBlur();
                      setIsInputFocused(false);
                    }}
                  />
                )}
              />
            </FieldWrapper>
          </Question>
          <ButtonContainer>
            <Button
              type="submit"
              aria-label={ctaButtonText}
              disabled={isInSubmittingState}
              variant="primary"
              isLoading={isInSubmittingState}
              horizontalPadding={32}
            >
              {ctaButtonText}
            </Button>
          </ButtonContainer>
        </BreedFormFieldsContainer>
      </BreedFormWrapper>
    </StyledFormWithImage>
  );
};

PupBreedForm.displayName = 'PupBreedForm';

PupBreedForm.propTypes = {
  defaultValues: PropTypes.shape({
    breeds: PropTypes.array,
  }).isRequired,
  goBack: PropTypes.func.isRequired,
  index: PropTypes.number.isRequired,
  labelBreed: PropTypes.string.isRequired,
  onSubmit: PropTypes.func.isRequired,
  subheadline: PropTypes.string,
  pupBreedQuestionCaption: PropTypes.string.isRequired,
  pupBreedQuestionPrompt: PropTypes.string.isRequired,
  pupBreedCallout: PropTypes.object,
  ctaButtonText: PropTypes.object,
  mobileImage: PropTypes.object.isRequired,
  desktopImage: PropTypes.object.isRequired,
  flowType: PropTypes.string,
};
